import {
	AMM_TO_QUOTE_PRECISION_RATIO,
	BASE_PRECISION,
	BASE_PRECISION_EXP,
	BigNum,
	BN,
	calculateEstimatedEntryPriceWithL2,
	isVariant,
	MarketType,
	PEG_PRECISION,
	PositionDirection,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION,
	SpotMarketConfig,
} from '@drift-labs/sdk';
import { ENUM_UTILS, MarketId, UIOrderType } from '@drift/common';
import Env, {
	SPOT_MARKETS_LOOKUP,
} from '../environmentVariables/EnvironmentVariables';
import useDriftStore, { DriftStore } from '../stores/DriftStore/useDriftStore';
import useMarketStateStore, {
	MarketStateStore,
} from '../stores/useMarketStateStore';
import NumLib from '../utils/NumLib';
import useInterval from './useInterval';
import { GetState } from 'zustand/vanilla';

export type PriceImpactInfo = {
	marketIndex: number;
	marketType: MarketType;
	entryPrice: BN;
	priceImpact: BN;
	baseAvailable: BN;
	bestPrice: BN;
	worstPrice: BN;
	// Because usePriceImpact is expensive and we use a refresh speed to limit how often it runs, return the "input" base size so that we know which input basesize the output value is for
	priceImpactInputBaseSize: BN;
	showPriceEstimateOracleDivergenceWarning: boolean;
};

export const getPriceImpactForTargetMarket = (
	tradeInfo: {
		marketIndex: number;
		marketType: MarketType;
		side: 'buy' | 'sell';
		quoteSize: BN;
		leadSide: 'quote' | 'base';
		baseSize: BN;
		uiOrderType: UIOrderType;
	},
	getState: GetState<DriftStore>,
	getMarketStateStore: GetState<MarketStateStore>,
	oraclePrice: BigNum
): PriceImpactInfo => {
	const { side, quoteSize, leadSide, baseSize } = tradeInfo;

	const l2State = getMarketStateStore().getDlobStateForMarket(
		new MarketId(tradeInfo.marketIndex, tradeInfo.marketType)
	);

	const driftClient = getState().driftClient?.client;

	const getPriceImpact = (): PriceImpactInfo => {
		if (!oraclePrice) return;
		if (!driftClient) return;

		const direction =
			side === 'buy' ? PositionDirection.LONG : PositionDirection.SHORT;

		const ZERO = new BN(0);
		let [entryPrice, priceImpact, baseFilled, bestPrice, worstPrice] = [
			ZERO,
			ZERO,
			ZERO,
			ZERO,
			ZERO,
		];

		if (isVariant(tradeInfo.marketType, 'perp')) {
			const marketAccount = driftClient.getPerpMarketAccount(
				tradeInfo.marketIndex
			);

			try {
				if (leadSide === 'base') {
					if (baseSize.lt(marketAccount.amm.baseAssetReserve)) {
						const entryResult = calculateEstimatedEntryPriceWithL2(
							leadSide,
							baseSize,
							direction,
							BASE_PRECISION,
							l2State
						);

						entryPrice = entryResult?.entryPrice || ZERO;
						priceImpact = entryResult?.priceImpact || ZERO;
						baseFilled = entryResult?.baseFilled || ZERO;
						bestPrice = entryResult?.bestPrice || ZERO;
						worstPrice = entryResult?.worstPrice || ZERO;
					}
				} else {
					if (
						quoteSize.lt(
							marketAccount.amm.quoteAssetReserve
								.mul(marketAccount.amm.pegMultiplier)
								.div(AMM_TO_QUOTE_PRECISION_RATIO)
								.div(PEG_PRECISION)
						)
					) {
						const entryResult = calculateEstimatedEntryPriceWithL2(
							leadSide,
							quoteSize,
							direction,
							BASE_PRECISION,
							l2State
						);
						entryPrice = entryResult?.entryPrice || ZERO;
						priceImpact = entryResult?.priceImpact || ZERO;
						baseFilled = entryResult?.baseFilled || ZERO;
						bestPrice = entryResult?.bestPrice || ZERO;
						worstPrice = entryResult?.worstPrice || ZERO;
					}
				}
			} catch (e) {
				// sdk function may throw error if no liquidity, do nothing
			}
		} else {
			const marketAccount = driftClient.getSpotMarketAccount(
				tradeInfo.marketIndex
			);

			try {
				if (marketAccount) {
					const entryResult = calculateEstimatedEntryPriceWithL2(
						leadSide,
						leadSide === 'base' ? baseSize : quoteSize,
						direction,
						SPOT_MARKETS_LOOKUP[tradeInfo.marketIndex].precision,
						l2State
					);

					entryPrice = entryResult?.entryPrice || ZERO;
					priceImpact = entryResult?.priceImpact || ZERO;
					baseFilled = entryResult?.baseFilled || ZERO;
					bestPrice = entryResult?.bestPrice || ZERO;
					worstPrice = entryResult?.worstPrice || ZERO;
				}
			} catch (e) {
				// sdk function may throw error if no liquidity, do nothing
			}
		}

		const thresholdPct = BigNum.from(entryPrice, PRICE_PRECISION_EXP).scale(
			Env.priceDivergenceWarningThreshold,
			100
		);
		const oracleDivergence = BigNum.from(
			entryPrice.sub(oraclePrice.val),
			PRICE_PRECISION_EXP
		).abs();
		const isBadPriceDivergence =
			(ENUM_UTILS.match(direction, PositionDirection.LONG) &&
				entryPrice.gt(oraclePrice.val)) ||
			(ENUM_UTILS.match(direction, PositionDirection.SHORT) &&
				entryPrice.lt(oraclePrice.val));
		const showPriceEstimateOracleDivergenceWarning =
			tradeInfo.uiOrderType === 'market' &&
			(baseSize.gt(ZERO) ? oracleDivergence.gt(thresholdPct) : false) &&
			isBadPriceDivergence;

		return {
			marketIndex: tradeInfo.marketIndex,
			marketType: tradeInfo.marketType,
			entryPrice,
			priceImpact,
			baseAvailable: baseFilled,
			bestPrice,
			worstPrice,
			priceImpactInputBaseSize: baseSize,
			showPriceEstimateOracleDivergenceWarning,
		};
	};

	const priceImpact = getPriceImpact();

	return priceImpact;
};

export const useKeepTradeformPriceImpactInSync = (refreshSpeedMs = 1000) => {
	const selectedMarketId = useDriftStore(
		(s) => s.selectedMarket.current.marketId
	);
	const selectedMarketConfig = useDriftStore(
		(s) => s.selectedMarket.current.market
	);

	const marketId = new MarketId(
		selectedMarketId.marketIndex,
		selectedMarketId.marketType
	);

	const getOraclePriceForMarket = useMarketStateStore(
		(s) => s.getOraclePriceForMarket
	);

	const setState = useDriftStore((s) => s.set);
	const getState = useDriftStore((s) => s.get);
	const getDlobState = useMarketStateStore((s) => s.get);

	const side = useDriftStore((s) => s.tradeForm.side);
	const quoteSizeStringValue = useDriftStore(
		(s) => s.tradeForm.quoteSizeStringValue
	);
	const quoteSize = NumLib.formatNum.toRawBn(
		Number(quoteSizeStringValue),
		QUOTE_PRECISION
	);
	const leadSide = useDriftStore((s) => s.tradeForm.leadSide);
	const baseSizeStringValue = useDriftStore(
		(s) => s.tradeForm.baseSizeStringValue
	);
	const baseSize = BigNum.fromPrint(
		baseSizeStringValue,
		selectedMarketId.isPerp
			? BASE_PRECISION_EXP
			: (selectedMarketConfig as SpotMarketConfig).precisionExp
	).val;
	const uiOrderType = useDriftStore((s) => s.tradeForm.orderType);

	useInterval(() => {
		const oraclePrice = getOraclePriceForMarket(marketId);
		const priceImpact = getPriceImpactForTargetMarket(
			{
				side,
				quoteSize,
				leadSide,
				baseSize,
				marketIndex: selectedMarketId?.marketIndex,
				marketType: selectedMarketId?.marketType,
				uiOrderType,
			},
			getState,
			getDlobState,
			oraclePrice
		);

		setState((s) => {
			if (
				priceImpact &&
				(!priceImpact?.entryPrice.eq(s.tradeForm.priceImpact?.entryPrice) ||
					!priceImpact?.priceImpactInputBaseSize.eq(
						s.tradeForm.priceImpact?.priceImpactInputBaseSize
					))
			) {
				s.tradeForm.priceImpact = priceImpact;
			}
		});
	}, refreshSpeedMs);
};

export const useTradeformPriceImpact = () => {
	const tradeFormPriceImpact = useDriftStore((s) => s.tradeForm.priceImpact);

	return tradeFormPriceImpact;
};
